import { TutorialPlacement } from './tutorialSteps';

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Kept clear around the card at the screen's edges. */
const MARGIN = 16;
/** Between the card and what it points at. */
const GAP = 14;
/** A target covering more of the screen than this has no "beside" worth
 *  using - the card sits inside it, in the bottom-right corner. */
const LARGE_TARGET_SHARE = 0.35;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Where the tutorial card goes (top-left corner, viewport coordinates): beside
 * the target on the preferred side if it fits there, else on the first side
 * that does; inside a large target; in the middle with no target at all.
 */
export const placeCard = (
  target: Box | null,
  card: Size,
  viewport: Size,
  preferred: TutorialPlacement = 'bottom'
): { top: number; left: number } => {
  const maxTop = viewport.height - card.height - MARGIN;
  const maxLeft = viewport.width - card.width - MARGIN;

  if (!target) {
    return {
      top: clamp((viewport.height - card.height) / 2, MARGIN, maxTop),
      left: clamp((viewport.width - card.width) / 2, MARGIN, maxLeft),
    };
  }

  const insideCorner = {
    top: clamp(target.top + target.height - card.height - 24, MARGIN, maxTop),
    left: clamp(target.left + target.width - card.width - 24, MARGIN, maxLeft),
  };
  if (target.width * target.height > LARGE_TARGET_SHARE * viewport.width * viewport.height) {
    return insideCorner;
  }

  const centredLeft = target.left + target.width / 2 - card.width / 2;
  const centredTop = target.top + target.height / 2 - card.height / 2;
  const candidates: Record<TutorialPlacement, { top: number; left: number; fits: boolean }> = {
    bottom: {
      top: target.top + target.height + GAP,
      left: clamp(centredLeft, MARGIN, maxLeft),
      fits: target.top + target.height + GAP <= maxTop,
    },
    top: {
      top: target.top - GAP - card.height,
      left: clamp(centredLeft, MARGIN, maxLeft),
      fits: target.top - GAP - card.height >= MARGIN,
    },
    right: {
      top: clamp(centredTop, MARGIN, maxTop),
      left: target.left + target.width + GAP,
      fits: target.left + target.width + GAP <= maxLeft,
    },
    left: {
      top: clamp(centredTop, MARGIN, maxTop),
      left: target.left - GAP - card.width,
      fits: target.left - GAP - card.width >= MARGIN,
    },
  };

  const order: TutorialPlacement[] = [preferred, 'bottom', 'top', 'right', 'left'];
  const side = order.find((placement) => candidates[placement].fits);
  if (!side) return insideCorner;

  const { top, left } = candidates[side];
  return { top, left };
};
